/**
 * Web app portal - orders, quotes and approvals (SD-S2-19, SD-S2-20): `/chat/<slug>/orders`, `/quotes`, `/approvals` and the hosted
 * confirm page a Messenger link opens. Words follow `mockups/tickets/webapp-portal.html` (P2-P4) and the widget's ordering card;
 * what the server itself says (a price-change sentence, why an order is refused, the "not an invoice" line) is shown as sent.
 */
export const webappPortalOrders = {
  nav: { area: "Orders and quotes", orders: "Orders", quotes: "Quotes", approvals: "Approvals" },
  area: { title: "Orders", intro: "Your orders, quotations and the approvals that wait for you." },
  off: { title: "Ordering is not available here", body: "This workspace does not take orders in the portal. You can still raise a request and we will help.", cta: "Go to my tickets" },
  common: {
    back: "Back",
    loadMore: "Show more",
    loadingMore: "Loading…",
    retry: "Try again",
    total: "Total",
    subtotal: "Subtotal",
    vat: "VAT",
    vatEstimated: "VAT (estimated)",
    inclVat: "incl. VAT",
    unpriced: "Price on request",
    listPrice: "List price",
    qty: "Qty",
    product: "Product",
    unitPrice: "Unit price",
    lineTotal: "Total",
    lines: "{{count}} lines",
    notInvoice: "This is not an invoice.",
    reference: "Reference",
    status: "Status",
    placed: "Placed",
    created: "Created",
    none: "—",
    offline: "You seem to be offline. Check your connection and try again.",
    limited: "Too many tries. Please wait a little and try again.",
    failed: "Something went wrong on our side. Please try again.",
    notFound: "We could not find that. It may belong to someone else or no longer exist.",
  },
  status: {
    draft: "Draft",
    pending_confirmation: "Waiting for your confirmation",
    pending_approval: "Waiting for approval",
    approved: "Approved",
    submitting: "Sending",
    placed: "Placed",
    processing: "Processing",
    partially_shipped: "Partly shipped",
    shipped: "Shipped",
    delivered: "Delivered",
    completed: "Completed",
    declined: "Declined",
    cancel_requested: "Cancellation requested",
    cancelled: "Cancelled",
    rejected: "Not accepted",
    sync_failed: "Being checked",
  },
  quoteStatus: { requested: "Waiting for a price", quoted: "Ready to accept", accepted: "Accepted", declined: "Declined", expired: "Expired", converted: "Ordered" },
  approvalStatus: { pending: "Waiting", approved: "Approved", declined: "Declined", expired: "Expired", withdrawn: "Withdrawn" },
  list: {
    title: "My orders",
    new: "New order",
    filter: "Show",
    all: "All orders",
    open: "In progress",
    done: "Delivered",
    cancelled: "Cancelled",
    emptyTitle: "No orders yet",
    emptyBody: "When you place an order it shows here with its status and tracking.",
    emptyFiltered: "No orders match this filter.",
    errorTitle: "We could not load your orders",
    po: "PO {{po}}",
    open_: "Open order {{number}}",
    pendingTitle: "Waiting for your confirmation",
  },
  detail: {
    title: "Order {{number}}",
    back: "All orders",
    items: "Items",
    totals: "Totals",
    delivery: "Delivery",
    site: "Site",
    requestedDate: "Requested date",
    poNumber: "PO number",
    paymentTerms: "Payment terms",
    notes: "Notes",
    tracking: "Tracking",
    carrier: "Carrier",
    trackingNumber: "Tracking number",
    expected: "Expected delivery",
    delayed: "Later than expected",
    invoices: "Invoices",
    checked: "Checked {{when}}",
    timeline: "What happened",
    refresh: "Check status",
    refreshed: "Status checked",
    shipped: "{{shipped}} of {{qty}} sent",
    rejected: "The order was not accepted: {{reason}}",
    errorTitle: "We could not load this order",
    notFoundTitle: "Order not found",
    confirmNow: "This order is waiting for your confirmation.",
    reviewConfirm: "Review and confirm",
  },
  events: {
    created: "Order started",
    reviewed: "You reviewed the order",
    price_changed: "A price changed",
    confirmed: "You confirmed the order",
    approval_required: "Sent for approval",
    queued: "Waiting to be sent",
    note: "Note from the team",
    approval_decided: "Approval decided",
    submit_attempt: "Sent to the order system",
    reconciled: "Checked with the order system",
    external_status: "Status update",
    delayed: "Delivery is later than expected",
  },
  reject: { out_of_stock: "an item is out of stock", price_changed: "a price changed", credit_hold: "the account is on hold", invalid_customer: "the customer is not set up", other: "the order system could not take it" },
  cancel: {
    button: "Cancel this order",
    requestButton: "Ask to cancel this order",
    title: "Cancel order {{number}}?",
    withdraw: "This order has not been sent yet. It is cancelled right away.",
    system: "We will ask the order system to cancel it.",
    request: "We will ask our team to cancel it. You will hear back.",
    notAllowed: "This order can no longer be cancelled here. Report a problem and we will help.",
    already: "A cancellation was already asked for. We will tell you what the team decides.",
    reason: "Reason (optional)",
    keep: "Keep the order",
    confirm: "Yes, cancel",
    done: "Order {{number}} is cancelled.",
    requested: "Cancellation requested. {{ticket}}",
    ticket: "Follow it in ticket {{number}}.",
  },
  issue: {
    button: "Problem with this order",
    title: "Problem with order {{number}}",
    intro: "Tell us what is wrong. We open a request for the team and link it to this order.",
    kind: "What is wrong?",
    note: "Details (optional)",
    lines: "Which items?",
    submit: "Send to the team",
    done: "Thank you. We opened request {{number}} for the team.",
    openRequest: "Open request {{number}}",
    related: "Requests about this order",
    kinds: { wrong_item: "Wrong item", missing_quantity: "Missing quantity", damaged: "Damaged", late: "Late delivery", invoice: "Invoice question", other: "Something else" },
  },
  new: {
    title: "New order",
    searchLabel: "Search the catalogue",
    searchPlaceholder: "Name, SKU or brand",
    searchHelp: "Type at least 2 letters. Prices shown are list prices until you review the order.",
    results: "Products",
    noResults: "No products match “{{q}}”.",
    searchError: "We could not search the catalogue.",
    paused: "Ordering is paused right now. You can browse the catalogue and ask for a quotation.",
    add: "Add",
    added: "{{name}} added",
    cart: "Your cart",
    cartEmpty: "Your cart is empty",
    cartEmptyBody: "Search for a product and add it to start an order.",
    remove: "Remove {{name}}",
    qtyOf: "Quantity of {{name}}",
    increase: "More of {{name}}",
    decrease: "Fewer of {{name}}",
    clear: "Empty the cart",
    checkout: "Delivery details",
    site: "Delivery site",
    address: "Site address",
    po: "PO number",
    date: "Requested date",
    notes: "Notes for the team",
    review: "Review order",
    reviewing: "Checking prices…",
    reviewTitle: "Review your order",
    editCart: "Back to the cart",
    inStock: "In stock",
    lowStock: "Low stock",
    out: "Out of stock",
    madeToOrder: "Made to order",
    unknown: "Availability on request",
    minQty: "Minimum {{qty}}",
  },
  review: {
    title: "Review your order",
    number: "Order {{number}}",
    delivery: "Delivery",
    degraded: "Some prices are list prices because the system did not answer in time.",
    stepUp: "This total needs a one-time code before it is sent.",
    approval: "This order needs an approver's OK before it is sent to us.",
    hint: "Confirming is a button press by you. Typing “yes” in a chat never places an order.",
    confirm: "Confirm order",
    confirmNew: "Confirm with new prices",
    confirmApproval: "Confirm and send for approval",
    confirming: "Placing your order…",
    changedTitle: "Prices changed",
    changedBody: "Some prices changed since you last looked. Check the new total before you confirm.",
    ack: "I have reviewed the new prices.",
    expiredTitle: "This confirmation has expired",
    expiredBody: "For your safety a confirmation lasts 30 minutes and works once. Review the order again to get a new one.",
    again: "Review again",
    blocked: "We could not place this order",
    cancel: "Cancel this order",
    cancelling: "Cancelling…",
    cancelled: "This order was cancelled. Nothing was sent.",
    placedTitle: "Thank you! Your order is in.",
    placedReplayed: "This order was already placed.",
    approvalTitle: "Sent for approval",
    approvalBody: "Order {{number}} waits for an approver. We tell you as soon as they decide.",
    viewOrder: "View the order",
    allOrders: "All orders",
  },
  stepUp: {
    title: "Enter your code",
    sent: "We sent a six-digit code to {{email}}.",
    label: "One-time code",
    verify: "Verify and continue",
    resend: "Send a new code",
    sending: "Sending…",
    bad: "That code did not work. Check the six digits and try again.",
    back: "Back",
  },
  quotes: {
    title: "My quotations",
    ask: "Ask for a quotation",
    emptyTitle: "No quotations yet",
    emptyBody: "Ask for a price on a bulk order or something special and the team replies here.",
    errorTitle: "We could not load your quotations",
    validUntil: "Valid until {{date}}",
    detailTitle: "Quotation {{number}}",
    back: "All quotations",
    request: "What you asked for",
    lines: "Quoted items",
    accept: "Accept quotation",
    acceptNew: "Accept with the new prices",
    decline: "Decline",
    declineTitle: "Decline quotation {{number}}?",
    declineReason: "Reason (optional)",
    declineConfirm: "Yes, decline",
    update: "Ask for an updated quote",
    updateDone: "We asked the team to price it again.",
    expiredNote: "This quotation has expired. The prices may have changed.",
    ticket: "Followed in request {{number}}",
    order: "Order {{number}}",
    accepted: "Quotation accepted.",
    needsStaff: "Quotation accepted. Some items are priced by hand, so the team will place the order and tell you.",
    declined: "Quotation declined.",
    priceChanges: "Some prices changed since this quotation",
    priceChangesBody: "Check the changes below. Accepting again takes the new prices.",
    review: "Confirm the order from this quotation",
    askTitle: "Ask for a quotation",
    askIntro: "List the products and quantities, or describe what you need. Nothing is sent until you press the button.",
    askText: "What do you need?",
    askPlaceholder: "e.g. Nitrile gloves, medium, box of 100",
    askQty: "Quantity",
    askNotes: "Anything else the team should know? (optional)",
    askSend: "Send request",
    askSent: "Request {{number}} sent. The team will price it and reply here.",
    askRequired: "Say what you need.",
  },
  approvals: {
    title: "Approvals",
    tabPending: "Waiting",
    tabDecided: "Decided",
    emptyPending: "Nothing waits for approval",
    emptyPendingBody: "When a colleague's order needs your OK it shows here.",
    emptyDecided: "No decisions yet",
    emptyDecidedBody: "Approved and declined orders show here.",
    errorTitle: "We could not load the approvals",
    requestedBy: "Requested by {{name}}",
    mine: "Your order",
    expires: "Expires {{date}}",
    open: "Review",
    detailTitle: "Approval for order {{number}}",
    reasons: "Why it needs approval",
    reason: {
      contact_limit: "Above the buyer's limit",
      org_threshold: "Above the organisation's threshold",
      staff_threshold: "Above the amount our team approves",
      credit_status: "The account is on credit hold",
      line_category: "An item needs approval",
      anomaly: "Unusual order",
    },
    note: "Note (optional)",
    approve: "Approve",
    approving: "Approving…",
    decline: "Decline",
    declineTitle: "Decline this order?",
    declineReason: "Reason",
    declineRequired: "Say why, so the buyer knows.",
    declineConfirm: "Yes, decline",
    withdraw: "Take back my order",
    needsReview: "Prices changed",
    needsReviewBody: "Prices were read again and are about {{percent}}% higher. Check the new total, then approve.",
    outOfStock: "Out of stock now: {{skus}}",
    ack: "I have checked the new prices.",
    approved: "Approved. The order is on its way to us.",
    declinedDone: "Declined. The buyer was told.",
    withdrawn: "Your order was taken back.",
    cannotDecide: "You can follow this order here. An approver decides it.",
    decidedBy: "{{status}} by {{name}} on {{date}}",
    stepUp: "Approving a large total needs a one-time code first.",
  },
  confirm: {
    title: "Confirm your order",
    intro: "You started this order in a chat. Check it, then press the button.",
    missing: "This link does not say which order it is for.",
    missingBody: "Open your orders to find it, or ask the assistant again.",
    loadError: "We could not load this order",
    notWaiting: "This order is no longer waiting for your confirmation.",
    viewOrders: "My orders",
  },
} as const;
